import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { BarChart3, Save } from "lucide-react";
import { apiGet, protectedPost } from "@/lib/api";
import { cn } from "@/lib/utils";
import { ModulePage, Label, INPUT, BTN_PRIMARY, Stat, NotConnected, fmtNum, fmtTime } from "@/components/common/ModuleShell";

function Budget({ label, used, limit, testId }) {
  const pct = limit ? Math.min(100, Math.round((used / limit) * 100)) : null;
  return (
    <div className="mb-3" data-testid={testId}>
      <div className="flex justify-between font-mono text-[10px] uppercase tracking-wider text-slate-500 mb-1"><span>{label}</span><span>{limit ? `${fmtNum(used)} / ${fmtNum(limit)} tokens (${pct}%)` : "no budget set"}</span></div>
      <div className="h-1.5 bg-white/5 rounded overflow-hidden"><div className={cn("h-full", pct >= 90 ? "bg-jarvis-red" : pct >= 70 ? "bg-jarvis-amber" : "bg-jarvis-cyan")} style={{ width: `${pct || 0}%` }} /></div>
    </div>
  );
}

function BudgetForm({ budget, onSaved }) {
  const [daily, setDaily] = useState(""), [monthly, setMonthly] = useState(""), [busy, setBusy] = useState(false);
  useEffect(() => { setDaily(budget?.daily_tokens ?? ""); setMonthly(budget?.monthly_tokens ?? ""); }, [budget]);
  const save = async () => {
    setBusy(true);
    try { await protectedPost("/ai-usage/budget", { daily_tokens: daily ? Number(daily) : null, monthly_tokens: monthly ? Number(monthly) : null }); toast.success("Budget saved."); onSaved(); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <div className="grid gap-3 md:grid-cols-[1fr_1fr_auto] items-end" data-testid="budget-form">
      <div><Label>Daily token budget</Label><input data-testid="budget-daily" type="number" className={INPUT} value={daily} onChange={(e) => setDaily(e.target.value)} placeholder="e.g. 200000" /></div>
      <div><Label>Monthly token budget</Label><input data-testid="budget-monthly" type="number" className={INPUT} value={monthly} onChange={(e) => setMonthly(e.target.value)} placeholder="e.g. 5000000" /></div>
      <button onClick={save} disabled={busy} className={BTN_PRIMARY} data-testid="budget-save"><Save className="w-3.5 h-3.5" />Save</button>
    </div>
  );
}

function Breakdown({ groups }) {
  if (!groups.length) return <p className="text-[12px] text-slate-500" data-testid="breakdown-empty">No AI requests recorded this month yet.</p>;
  return (
    <table className="w-full text-left" data-testid="breakdown-table">
      <thead><tr className="font-mono text-[10px] uppercase tracking-wider text-slate-500"><th className="py-1">Provider</th><th>Model</th><th>Kind</th><th className="text-right">Requests</th><th className="text-right">Tokens</th></tr></thead>
      <tbody>{groups.map((g) => <tr key={`${g.provider}-${g.model}-${g.kind}`} className="border-t border-white/5 font-num text-[12px] text-slate-300">
        <td className="py-1.5">{g.provider}</td><td className="font-code text-[11px]">{g.model}</td><td>{g.kind}</td><td className="text-right">{fmtNum(g.requests)}</td>
        <td className="text-right">{fmtNum(g.tokens)}{g.unmeasured ? <span className="text-slate-600"> (+{g.unmeasured} unmeasured)</span> : null}</td></tr>)}</tbody>
    </table>
  );
}

export default function AIUsagePage() {
  const [data, setData] = useState(null), [limits, setLimits] = useState(null);
  const load = useCallback(() => Promise.all([apiGet("/ai-usage/ledger"), apiGet("/ai/limits")]).then(([d, l]) => { setData(d); setLimits(l); }).catch((e) => toast.error(e.message)), []);
  useEffect(() => { load(); const id = setInterval(load, 15000); return () => clearInterval(id); }, [load]);
  const b = data?.budget || {};
  return (
    <ModulePage icon={BarChart3} title="AI Usage" testId="ai-usage-page"
      subtitle={data ? `${fmtNum(data.today.requests)} requests today · ${fmtNum(data.month.tokens)} tokens this month · billing not connected` : "Loading…"}>
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4 mb-5">
        <Stat label="Requests today" value={fmtNum(data?.today.requests)} hint={limits ? `chat limit ${limits.chat.day.used}/${limits.chat.day.limit} · images ${limits.images.day.used}/${limits.images.day.limit}` : ""} testId="stat-requests-today" />
        <Stat label="Tokens today" value={fmtNum(data?.today.tokens)} hint={data?.today.unmeasured ? `${data.today.unmeasured} without usage data` : "from provider responses"} testId="stat-tokens-today" />
        <Stat label="Tokens · 7 days" value={fmtNum(data?.week.tokens)} hint={`${fmtNum(data?.week.requests)} requests`} testId="stat-tokens-week" />
        <Stat label="Spend" value="—" tone="muted" hint="billing not connected" testId="stat-spend" />
      </div>
      <div className="grid gap-5 xl:grid-cols-[1fr_420px]">
        <div className="jv-panel p-5" data-testid="breakdown-card">
          <div className="font-mono text-[12px] uppercase tracking-widest text-jarvis-cyanBright mb-3">This month by provider · model · kind</div>
          {data && <Breakdown groups={data.month.groups} />}
          <div className="mt-5 font-mono text-[12px] uppercase tracking-widest text-jarvis-cyanBright mb-2">Recent requests</div>
          <div className="space-y-1 max-h-64 overflow-y-auto" data-testid="recent-list">
            {data?.recent.length === 0 && <p className="text-[12px] text-slate-500">Nothing recorded yet.</p>}
            {data?.recent.map((r) => <div key={`${r.ts}-${r.model}-${r.kind}`} className="flex justify-between font-num text-[11px] text-slate-400"><span>{fmtTime(r.ts)} · {r.provider} · <span className="font-code">{r.model}</span> · {r.kind}</span><span>{r.prompt_tokens == null ? "unmeasured" : `${fmtNum(r.prompt_tokens)} in / ${fmtNum(r.completion_tokens)} out`}</span></div>)}
          </div>
        </div>
        <div className="space-y-5">
          <div className="jv-panel p-5" data-testid="budget-card">
            <div className="font-mono text-[12px] uppercase tracking-widest text-jarvis-cyanBright mb-3">Token budgets</div>
            <Budget label="Today" used={data?.today.tokens || 0} limit={b.daily_tokens} testId="budget-today" />
            <Budget label="This month" used={data?.month.tokens || 0} limit={b.monthly_tokens} testId="budget-month" />
            <BudgetForm budget={b} onSaved={load} />
          </div>
          <div className="jv-panel p-5" data-testid="cost-card">
            <div className="flex items-center justify-between mb-2"><div className="font-mono text-[12px] uppercase tracking-widest text-jarvis-cyanBright">Cost</div><NotConnected testId="cost-status">Billing not connected</NotConnected></div>
            <p className="text-[12px] text-slate-500">{data?.cost_note}</p>
          </div>
        </div>
      </div>
    </ModulePage>
  );
}
